import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';
import { FaInfoCircle } from 'react-icons/fa';
import { Coupon } from '../../../../types';
import { updatePaymentCoupon } from '../../../../../../../../crud/payments_api/payment_coupons';
import CouponType from '../CreateCoupon/components/CouponType/CouponType';
import CouponLimits from '../CreateCoupon/components/CouponLimits/CouponLimits';
import '../CreateCoupon/CreateCoupon.scss';

interface UpdateCouponProps {
  show: boolean;
  handleClose: () => void;
  coupons: Coupon[];
  setCoupon: (val: any) => void;
  coupon: Coupon;
  itemOptions: any[];
  menuOptions: any[];
  categoryOptions: any[];
}

const UpdateCoupon: React.FC<UpdateCouponProps> = ({
  show,
  handleClose,
  coupons,
  setCoupon,
  coupon,
  menuOptions,
  categoryOptions,
  itemOptions
}) => {
  // Core states (mirroring CreateCoupon but initialized from existing coupon)
  const [couponName, setCouponName] = useState('');
  const [couponType, setCouponType] = useState('');
  const [limitType, setLimitType] = useState('Sin limite');
  const [applyOn, setApplyOn] = useState('Global');
  const [maxNumber, setMaxNumber] = useState<number | null>(null);
  const [maxBudget, setMaxBudget] = useState<number | null>(null);
  const [isGlobal, setIsGlobal] = useState(true);
  const [categoryId, setCategoryId] = useState('');
  const [menuId, setMenuId] = useState('');
  const [itemId, setItemId] = useState('');
  const [couponAmount, setCouponAmount] = useState<number | null>(null);
  const [couponPercentage, setCouponPercentage] = useState<number | null>(null);
  const [amountError, setAmountError] = useState('');

  // Calendar & info banner
  const [selectedExpirationDate, setSelectedExpirationDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const [showAlcanceExplanation, setShowAlcanceExplanation] = useState('');

  // Selected entities for validation
  const selectedCategory = categoryOptions.find((c) => c.id === categoryId);
  const selectedItem = itemOptions.find((i) => i.id === itemId);

  // Initialize from coupon when opened / coupon changes
  useEffect(() => {
    if (!coupon) return;
    setCouponName(coupon.payment_coupon_name || '');
    setCouponType(coupon.coupon_amount ? 'Amount' : coupon.coupon_percentage ? 'Percentage' : '');
    const derivedLimit = coupon.expiration_date && coupon.max_budget
      ? 'Presupuesto y Fecha de Expiración'
      : coupon.expiration_date
        ? 'Fecha Expiración'
        : coupon.max_budget
          ? 'Presupuesto'
          : coupon.max_number
            ? 'Número de Usos'
            : 'Sin limite';
    setLimitType(derivedLimit);
    setApplyOn(
      coupon.menu_id ? 'Menú' : coupon.menu_category_id ? 'Categoría' : coupon.item_id ? 'Item' : 'Global'
    );
  setIsGlobal(!coupon.menu_id && !coupon.menu_category_id && !coupon.item_id);
    setMaxNumber(coupon.max_number || null);
    setMaxBudget(coupon.max_budget || null);
    setCategoryId(coupon.menu_category_id || '');
    setMenuId(coupon.menu_id || '');
    setItemId(coupon.item_id || '');
    setCouponAmount(coupon.coupon_amount || null);
    setCouponPercentage(coupon.coupon_percentage || null);
    setSelectedExpirationDate(coupon.expiration_date ? new Date(coupon.expiration_date) : null);
    setAmountError('');
    setShowCalendar(false);
    setShowInfoBanner(false);
    setShowAlcanceExplanation('');
  }, [coupon, show]);

  // Amount validation (same logic as CreateCoupon)
  useEffect(() => {
    if (couponType === 'Amount' && couponAmount !== null && selectedItem && selectedItem.price !== undefined) {
      if (couponAmount > selectedItem.price) {
        setAmountError('El monto del descuento no puede ser mayor que el valor del ítem seleccionado.');
      } else {
        setAmountError('');
      }
    } else {
      setAmountError('');
    }
  }, [couponAmount, selectedItem, couponType]);

  const handleCloseModal = () => {
    handleClose();
  };

  const handleUpdateCoupon = async () => {
    try {
      // Mantener la lógica original de sumar un día a la expiración (si así se venía usando)
      let formattedExpiration = '';
      if (selectedExpirationDate) {
        const datePlusOne = new Date(selectedExpirationDate);
        datePlusOne.setDate(datePlusOne.getDate() + 1);
        formattedExpiration = datePlusOne.toISOString().split('T')[0];
      }
      const payload: any = {
        payment_coupon_id: coupon.payment_coupon_id,
        payment_coupon_name: couponName,
        expiration_date: formattedExpiration === '' ? null : formattedExpiration,
  isvalid: true,
        max_number: maxNumber,
        max_budget: maxBudget,
        isglobal: isGlobal,
        menu_category_id: categoryId === '' ? null : categoryId,
        menu_id: menuId === '' ? null : menuId,
        item_id: itemId === '' ? null : itemId,
        local_id: coupon.local_id,
        coupon_amount: couponAmount,
        coupon_percentage: couponPercentage,
      };
      if (couponType === 'Percentage') payload.coupon_amount = null;
      if (couponType === 'Amount') payload.coupon_percentage = null;
      if (limitType === 'Fecha Expiración') { payload.max_budget = null; payload.max_number = null; }
      if (limitType === 'Presupuesto') { payload.expiration_date = null; payload.max_number = null; }
      if (limitType === 'Número de Usos') { payload.expiration_date = null; payload.max_budget = null; }
      if (limitType === 'Presupuesto y Fecha de Expiración') { payload.max_number = null; }
      if (limitType === 'Sin limite') { payload.expiration_date = null; payload.max_budget = null; payload.max_number = null; }

      const response = await updatePaymentCoupon(coupon.payment_coupon_id, payload);
      const updated = (response as any).data;
      setCoupon(coupons.map(c => c.payment_coupon_id === coupon.payment_coupon_id ? updated : c));
      toast.success('Cupón actualizado exitosamente');
      handleClose();
    } catch (error: any) {
      if (error?.response?.data?.message === 'The coupon amount is greater than the price of the item') {
        setAmountError('El monto del descuento no puede ser mayor o igual al valor del ítem seleccionado.');
      } else if (error?.status === 401) {
        toast.error('La fecha de expiración no puede ser anterior a la fecha actual');
      } else {
        toast.error('Error al actualizar el cupón');
      }
    }
  };

  return (
    <Modal show={show} onHide={handleCloseModal} className="modal-cupones" size="lg">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Actualizar cupón</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleCloseModal}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        {/* Banner informativo */}
        <div className="coupon-info-banner">
          <div className="banner-header" onClick={() => setShowInfoBanner(!showInfoBanner)}>
            <div className="header-content">
              <div className="header-icon">
                <FaInfoCircle />
              </div>
              <div className="header-title">¿Qué es un cupón?</div>
            </div>
            <div className={`toggle-icon ${showInfoBanner ? 'expanded' : 'collapsed'}`}>
              {showInfoBanner ? '−' : '+'}
            </div>
          </div>
          {showInfoBanner && (
            <div className="banner-content">
              Una plantilla sobre la cual se pueden generar cupones individuales que pueden ser compartidos con clientes. 
              Por ejemplo, si creas un cupón <strong>"VIP $1.000"</strong>, entonces luego podrás generar códigos QR para tus clientes usando esta plantilla.
            </div>
          )}
        </div>

        <Form className="coupon-form" onSubmit={(e) => { e.preventDefault(); handleUpdateCoupon(); }}>
          {/* Nombre del cupón */}
          <div className="form-section name-section">
            <Form.Group className="mp-formgroup">
              <Form.Control
                type="text"
                value={couponName}
                onChange={(e) => setCouponName(e.target.value)}
                required
                placeholder="Nombre del cupón (Ej: Descuento VIP, Promo Fin de Semana)"
                className="coupon-name-input"
              />
            </Form.Group>
          </div>

          {/* Tipo de cupón modularizado */}
          <CouponType
            couponType={couponType}
            setCouponType={setCouponType}
            couponAmount={couponAmount}
            setCouponAmount={setCouponAmount}
            couponPercentage={couponPercentage}
            setCouponPercentage={setCouponPercentage}
            amountError={amountError}
          />

          <CouponLimits
            limitType={limitType}
            setLimitType={setLimitType}
            selectedExpirationDate={selectedExpirationDate}
            setSelectedExpirationDate={setSelectedExpirationDate}
            showCalendar={showCalendar}
            setShowCalendar={setShowCalendar}
            maxBudget={maxBudget}
            setMaxBudget={setMaxBudget}
            maxNumber={maxNumber}
            setMaxNumber={setMaxNumber}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button className="form-button-cancel" variant="none" onClick={handleClose}>
            Cerrar
          </Button>
          <Button
            className="form-button-create"
            variant="none"
            onClick={() => {
              if (!couponType) {
                toast.error('Debes seleccionar el tipo de cupón.');
                return;
              }
              if (!!amountError) {
                toast.error('Corrige el error en el monto del descuento.');
                return;
              }
              handleUpdateCoupon();
            }}
          >
            Actualizar cupón
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateCoupon;

